import { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import type { Diamond } from '../api/types'
import { Diaex360 } from './Diaex360'
import { formatPriceNumber, formatPerCarat, formatCarat, displayStockId } from '../lib/format'
import { logEnquiry, searchApi, trackApi } from '../api/endpoints'
import { FavoriteStoneButton, FavoriteSupplierButton } from './FavoriteButtons'
import { AddToSourcingButton } from './sourcing/AddToSourcingButton'
import { stoneEnquiryText, stoneEnquirySubject, waHref, smsHref, mailtoHref, waTargetOf } from '../lib/contactMessage'
import { useApp } from '../store'
import { StoneImage } from './StoneImage'
import { supplierReturnsLine } from '../lib/supplierReturns'
import { videoKindOf } from '../lib/media'
import { labReportUrl } from '../lib/labReport'
import { toast } from '../lib/toast'

interface Props {
  open: boolean
  onClose: () => void
  stone: Diamond | null
  blurPrice?: boolean
  /** Step to the previous/next stone in the caller's list (arrows + chevrons). */
  onPrev?: () => void
  onNext?: () => void
}

/**
 * Full stone detail — premium two-pane modal matching the DiaEx mockup:
 * a NAVY media pane on the left (photo / mp4 / native 360 spin) and a
 * WHITE spec sheet on the right. useStoneImage always resolves a real
 * photo now, so there's no empty state / silhouette to fall back to.
 *
 * The list row already carries everything this modal renders: the search
 * list and /search/:id apply the SAME reveal gate + pricing, so a signed-in
 * buyer's row has identical supplier contact + mode + media to detail. So we
 * paint straight from the row and only refetch when the row is genuinely
 * incomplete — a signed-in buyer whose row is missing supplier contact
 * (a stale/anonymous row). That skips a network round-trip on the common open
 * AND on every prev/next hop, which is what made stepping through stones feel
 * heavy.
 */
export function MediaModal({ open, onClose, stone, blurPrice, onPrev, onNext }: Props) {
  const vidRef = useRef<HTMLVideoElement>(null)
  const [detail, setDetail] = useState<Diamond | null>(null)
  const [vidFailed, setVidFailed] = useState(false)
  const [vidReady, setVidReady] = useState(false)
  const [frameLoaded, setFrameLoaded] = useState(false)
  const [frameFailed, setFrameFailed] = useState(false)
  // Diaex360 (our native masked player) is tried first for viewer-page 360s;
  // this flips true when it can't resolve a frame sequence, dropping to the
  // legacy iframe.
  const [diaexFailed, setDiaexFailed] = useState(false)
  const [view, setView] = useState<'photo' | '360'>('photo')

  // Render the freshest data we have — the refetched detail once it
  // lands, the list-provided stone until then (so nothing flickers).
  const s = detail ?? stone

  // The photo pane is drawn by the shared <StoneImage/> (Tej 2026-09-12) —
  // the SAME component the grid/cards and the mobile detail carousel use, so
  // the detail modal now inherits its clean loading behaviour: a .skeleton
  // shimmer while the image (or a resolving 360 poster) paints, a crossfade
  // in, and the "image not available" watermark gated until we're CERTAIN
  // there's no real photo — never the bare-<img> pop this pane used to flash.
  // useStoneImage always resolves a real photo, so there's no empty state.

  // Reset per-stone media state whenever the stone changes.
  useEffect(() => {
    setDetail(null)
    setVidFailed(false); setVidReady(false)
    setFrameLoaded(false); setFrameFailed(false); setDiaexFailed(false)
    setView('photo')
  }, [stone?.uniqueId ?? stone?.stockId])

  // On open, refetch the full (authed) detail ONLY when the row looks
  // incomplete — a signed-in buyer (blurPrice=false) whose row is missing
  // supplier contact. The common case (row already revealed) skips the fetch
  // entirely, so opening and stepping prev/next don't hit the network. Failure
  // is silent — we keep showing the list-provided stone.
  const rowNeedsDetail =
    !blurPrice && !!stone && !(stone.supplier && (stone.supplier.phone || stone.supplier.email))
  // Recommender signal: log the open for signed-in users (feeds
  // "viewedNotBought"). getState() read — non-reactive is fine on open.
  useEffect(() => {
    if (open && stone && useApp.getState().signedIn) {
      trackApi.view(String(stone.stockId), 'desktop_modal')
    }
  }, [open, stone?.uniqueId ?? stone?.stockId])

  useEffect(() => {
    if (!open || !stone || !rowNeedsDetail) return
    let cancelled = false
    searchApi.detail(String(stone.uniqueId ?? stone.stockId))
      .then((d) => { if (!cancelled) setDetail(d) })
      .catch(() => { /* keep the list-provided stone */ })
    return () => { cancelled = true }
  }, [open, stone?.uniqueId ?? stone?.stockId])

  useEffect(() => {
    if (!open) return
    const orig = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowLeft' && onPrev) onPrev()
      else if (e.key === 'ArrowRight' && onNext) onNext()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = orig
      window.removeEventListener('keydown', onKey)
    }
  }, [open, onClose, onPrev, onNext])

  const videoUrl = s?.media?.videoUrl ?? null
  const vKind = videoKindOf(videoUrl)
  // useStoneImage always resolves a real photo (own → 360 frame → representative
  // → bundled floor), so the photo pane always has something to draw.
  const hasImage = true
  // 'spin' (the masked token an unverified caller gets) has NO iframe fallback:
  // the token is not a page, and iframing anything supplier-side would put the
  // host + ?d=<REF> back on the wire for exactly the caller who may not see it.
  //
  // But "no fallback" must not mean "no tab". Tying hasVideo to diaexFailed made
  // the 360° toggle DISAPPEAR mid-session on the ~2,400 stones whose spin
  // Diaex360 can't resolve — a control that silently evaporates reads as a bug
  // and loses the affordance entirely. The stone HAS a 360; we just can't render
  // it right now. So the tab stays and the pane says so (see spin360Unavailable
  // below), which is the honest, non-vanishing degradation.
  const spin360Unavailable = vKind === 'spin' && diaexFailed
  const hasVideo = !!vKind && !(
    vKind === 'mp4' ? vidFailed
    : vKind === 'spin' ? false
    : frameFailed
  )

  // Effective media to render: honour the toggle, but fall back to
  // whatever's actually available (a failed photo drops to 360, etc.).
  const media: 'photo' | '360' =
    view === '360' && hasVideo ? '360'
    : hasImage ? 'photo'
    : '360'

  // Watchdog — if the 360° viewer iframe never loads (or renders blank),
  // give up after a spell and drop to the photo.
  useEffect(() => {
    // Only guards the legacy iframe fallback; while Diaex360 is being tried
    // (its own resolve + progress ring) there's no iframe to watch.
    if (media !== '360' || vKind !== 'iframe' || !diaexFailed || frameLoaded || frameFailed) return
    const t = setTimeout(() => setFrameFailed(true), 12000)
    return () => clearTimeout(t)
  }, [media, vKind, diaexFailed, frameLoaded, frameFailed])

  return (
    <AnimatePresence>
      {open && s && (
        <motion.div
          /* Solid scrim — NO backdrop-filter. A backdrop-blur over the 50-row
             table forced the GPU to re-sample+blur the whole catalog every
             frame of the open/close fade (the interaction spike Tej felt). A
             slightly deeper solid rgba reads the same premium dim over a busy
             table at a fraction of the cost, and lets the fade run on the
             compositor. will-change:opacity promotes it so open/close paint
             once. */
          className="fixed inset-0 z-[60] flex items-center justify-center bg-[rgba(10,31,60,0.62)] p-4 [will-change:opacity]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.16, ease: 'easeOut' } }}
          /* The overlay IS the modal root, so AnimatePresence keeps it mounted
             until THIS exit finishes — it's what gates "modal gone". Keep it
             short (110ms) so CLOSE reads as instant; the nested panel exit is
             the same order. */
          exit={{ opacity: 0, transition: { duration: 0.11, ease: 'easeIn' } }}
          onClick={onClose}
          data-testid="media-modal"
        >
          {onPrev && (
            <button type="button" aria-label="Previous stone" data-testid="modal-prev"
              onClick={(e) => { e.stopPropagation(); onPrev() }}
              className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full bg-paper/10 text-white backdrop-blur-md hover:bg-paper/25 transition-colors">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
          )}
          {onNext && (
            <button type="button" aria-label="Next stone" data-testid="modal-next"
              onClick={(e) => { e.stopPropagation(); onNext() }}
              className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full bg-paper/10 text-white backdrop-blur-md hover:bg-paper/25 transition-colors">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
            </button>
          )}
          <motion.div
            /* Enter/leave on opacity + a small translate only — no `scale`.
               Scaling a tall, scrollable panel repaints the whole subtree each
               frame; a Y-translate composites. will-change:transform,opacity
               promotes it to its own layer so the tween never touches layout.
               Exit is deliberately short (120ms) so CLOSE reads as instant. */
            className="relative w-full max-w-4xl max-h-[92vh] overflow-y-auto rounded-lg bg-paper shadow-elev-4 [will-change:transform,opacity]"
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1, transition: { duration: 0.2, ease: [0.16, 1, 0.3, 1] } }}
            exit={{ y: 6, opacity: 0, transition: { duration: 0.12, ease: 'easeIn' } }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={`Stone detail #${displayStockId(s)}`}
          >
            <div className="grid grid-cols-1 md:grid-cols-2">
              {/* ─── LEFT column — media + (desktop) the seller/contact block.
                   Stacking the seller UNDER the image fills what used to be a
                   tall sea of white beside the specs, and balances the two
                   columns (Tej 2026-07-27). On mobile this column is JUST the
                   square image; the seller block renders in the right column
                   between price and Buy-with-Aska so the mobile order stays
                   image → specs → price → seller → buy. ─── */}
              <div className="flex flex-col md:border-r md:border-line/70">
              <div className="relative bg-gradient-to-br from-[#f6f8fb] to-[#e9eef4] md:rounded-tl-lg flex items-center justify-center p-3 md:p-5">
                <div className="relative aspect-square w-full overflow-hidden rounded-lg flex items-center justify-center">
                {/* NAVY PANE vs 360 LETTERBOX (constraint #3):
                    - PHOTO fills the navy pane with object-cover → no bands. ✓
                    - The 360 SPIN (Diaex360 / mp4 / legacy iframe) is object-contain,
                      so on a bare navy pane its margins would reintroduce the exact
                      navy letterbox bands buyers flagged. To avoid that WITHOUT
                      regressing the shipped 360 fix, the 360 renders inside a soft
                      LIGHT rounded card floating on the navy — the letterbox margins
                      land on that clean lightbox frame, never on raw navy. Diaex360's
                      own background (from-[#f6f8fb]) already matches this card, so its
                      progressive reveal + drag spin sit flush inside it. No Diaex360.tsx
                      change, no timeout change. */}

                {media === 'photo' && hasImage && (
                  /* Shared <StoneImage/> — owns the fallback chain, the
                     .skeleton loading shimmer, the crossfade-in, and the
                     "image not available" watermark (gated until certain), so
                     this pane behaves EXACTLY like the grid/cards and the
                     mobile detail carousel. `width=1024` picks the large rung
                     off the proxy's resize ladder — plenty for a ~450px pane
                     on a 2x display, a fraction of the full-size still — and
                     only touches our own /api/v URLs; raw KYC-revealed supplier
                     URLs and the bundled fallbacks pass through untouched.
                     object-COVER (constraint #3) fills the pane edge-to-edge
                     for every aspect; stones are centred in their frames so the
                     mild crop never touches the subject. */
                  <StoneImage
                    stone={s}
                    width={1024}
                    imgClassName="relative h-full w-full object-cover"
                  />
                )}

                {/* Soft light lightbox card — hosts every 360 variant so its
                    object-contain margins read as a premium frame, not navy bands. */}
                {media === '360' && (
                  <div className="absolute inset-4 md:inset-6 rounded-2xl bg-gradient-to-br from-[#f6f8fb] to-[#e9eef4] shadow-soft overflow-hidden">
                    {vKind === 'mp4' && (
                      <video
                        ref={vidRef}
                        src={videoUrl!}
                        controls autoPlay loop muted playsInline
                        className={
                          'absolute inset-0 w-full h-full object-contain transition-opacity duration-300 ' +
                          (vidReady ? 'opacity-100' : 'opacity-0')
                        }
                        onLoadedData={() => setVidReady(true)}
                        onCanPlay={() => setVidReady(true)}
                        onError={() => setVidFailed(true)}
                      />
                    )}

                    {/* Our own masked player first — native drag-to-rotate spin
                        served through /api/v (no supplier host/ref leak, no
                        archaic viewer chrome). For 'iframe' (a KYC-approved
                        caller holding the real viewer URL) it falls back to the
                        sandboxed iframe below; for 'spin' there is nothing to
                        fall back to and hasVideo goes false. */}
                    {(vKind === 'iframe' || vKind === 'spin') && !diaexFailed && (
                      <Diaex360 viewerUrl={videoUrl!} onUnavailable={() => setDiaexFailed(true)} />
                    )}

                    {/* Masked spin that wouldn't resolve. There is deliberately
                        nothing to fall back to — the token is not a page — so we
                        say so in place rather than deleting the 360° tab out
                        from under the buyer. Same copy as the server's /v/360
                        safety-net card, and the photo is one tap away. */}
                    {spin360Unavailable && (
                      <div
                        data-testid="spin-unavailable"
                        className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center"
                      >
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="text-navy-300" aria-hidden="true">
                          <ellipse cx="12" cy="12" rx="10" ry="4.6" />
                          <path d="M4.6 9.4A10 10 0 1 0 12 2" />
                        </svg>
                        <p className="text-[11px] font-bold uppercase leading-snug tracking-[0.14em] text-navy-300">
                          360° view unavailable
                        </p>
                        <button
                          type="button"
                          onClick={() => setView('photo')}
                          className="text-[11px] font-semibold text-primary underline underline-offset-2"
                        >
                          View photo instead
                        </button>
                      </div>
                    )}

                    {vKind === 'iframe' && diaexFailed && (
                      <iframe
                        title="360° view"
                        src={videoUrl!}
                        sandbox="allow-scripts allow-same-origin"
                        /* The viewer PAGE inside is square content on a white body —
                           oversize vertically + crop so its stage fills the card and
                           the white tail (and stray controls) fall below the fold. */
                        style={{ height: '135%' }}
                        className={
                          'absolute top-0 left-0 w-full border-0 bg-transparent transition-opacity duration-300 ' +
                          (frameLoaded ? 'opacity-100' : 'opacity-0')
                        }
                        onLoad={() => setFrameLoaded(true)}
                        onError={() => setFrameFailed(true)}
                      />
                    )}

                    {/* Loading affordance while the spin buffers / viewer loads. */}
                    {vKind === 'mp4' && !vidReady && <LoadingPill text="Loading 360° video…" />}
                    {vKind === 'iframe' && diaexFailed && !frameLoaded && <LoadingPill text="Loading 360°…" />}
                  </div>
                )}

                {/* Close button — top-right of the media pane, on navy. */}
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="absolute top-4 right-4 z-20 inline-flex h-9 w-9 items-center justify-center rounded-full bg-paper/10 text-white backdrop-blur-md hover:bg-paper/20 transition-colors md:hidden"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="6" y1="6" x2="18" y2="18" /><line x1="6" y1="18" x2="18" y2="6" />
                  </svg>
                </button>

                {/* (The mockup's decorative heart/share chips were removed
                    2026-08-02 — they looked tappable but did nothing. Dead
                    affordances erode trust; sharing lives in the catalog's
                    row-selection → Create share flow.) */}

                {/* Stone-type / 3EX chips — bottom-left. */}
                <div className="absolute bottom-4 left-4 z-20 flex flex-col gap-1 items-start">
                  <span className="rounded-md bg-paper/12 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white backdrop-blur-sm">
                    {s.stoneType || 'CVD'}
                  </span>
                  {s.tripleEx && (
                    <span className="rounded-md bg-gradient-to-r from-primary to-[#2a6cb8] px-2 py-0.5 text-[10px] font-bold text-white">
                      3EX
                    </span>
                  )}
                </div>

                {/* The substitute watermark is now drawn by <StoneImage/>
                    itself (same copy as the card overlay), so it stays gated
                    on imgLoaded — never flashed before the real photo has had
                    a chance to paint. No separate overlay here. */}

                {/* Photo / 360° toggle — only when both media exist. */}
                {hasImage && hasVideo && (
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 inline-flex rounded-full bg-paper/95 p-0.5 shadow-card backdrop-blur-sm" role="tablist" aria-label="Media view">
                    <MediaToggle active={media === 'photo'} onClick={() => setView('photo')}>Photo</MediaToggle>
                    <MediaToggle active={media === '360'} onClick={() => setView('360')}>360°</MediaToggle>
                  </div>
                )}
                </div>
              </div>{/* media pane */}

              {/* Desktop-only: the seller/contact block sits UNDER the image so
                  the left column isn't mostly white next to the tall spec sheet. */}
              {!blurPrice && s.supplier && (s.supplier.phone || s.supplier.email) && (
                <div className="hidden md:block px-6 pt-6 pb-8 lg:px-8">
                  <SupplierContact stone={s} />
                </div>
              )}
              </div>{/* LEFT column */}

              {/* ─── Spec sheet — white ─── */}
              <div className="relative p-6 md:p-8 space-y-5">
                {/* Desktop close — top-right of the white sheet. */}
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="hidden md:inline-flex absolute top-5 right-5 h-9 w-9 items-center justify-center rounded-full text-soft hover:bg-page hover:text-ink transition-colors"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="6" y1="6" x2="18" y2="18" /><line x1="6" y1="18" x2="18" y2="6" />
                  </svg>
                </button>

                <div className="flex items-start justify-between gap-3 md:pr-10">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-navy-300 font-bold">Stock ID</p>
                    {/* Serif is allowed here — the stone-detail hero name (spec #7). */}
                    <p className="font-serif text-3xl font-bold text-ink leading-tight mt-0.5 tracking-h1">#{displayStockId(s)}</p>
                    <p className="text-sm text-navy-500 mt-1">
                      {s.shape} · {formatCarat(s.carat)}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {s.lab && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-success/30 bg-success/10 px-2.5 py-1 text-[10px] font-bold text-success uppercase tracking-wider">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2 4 7v10l8 5 8-5V7l-8-5Z" /></svg>
                        {s.lab}
                      </span>
                    )}
                    {/* Add to sourcing (self-gates to nothing off-flag) next to
                        the save-for-later bookmark — the two distinct verbs
                        side by side (go-get-it vs save). */}
                    <AddToSourcingButton stone={s} />
                    <FavoriteStoneButton stone={s} />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-x-3 gap-y-4 rounded-md border border-line bg-cream p-4">
                  <Spec label="Color" value={s.color} />
                  <Spec label="Clarity" value={s.clarity} />
                  <Spec label="Fluorescence" value={s.fluorescence || 'None'} />
                  <Spec label="Cut" value={s.cut} />
                  <Spec label="Polish" value={s.polish} />
                  <Spec label="Symmetry" value={s.symmetry} />
                  {s.measurements?.ratio != null && (
                    <Spec label="Ratio" value={s.measurements.ratio.toFixed(2)} />
                  )}
                  {/* Certificate proportions (gap #93) — depth % prefers the
                      SUPPLIED certificate value server-side (specs.depthPct);
                      table % + girdle are supplied-only. Rendered only when
                      they exist — never a dash. */}
                  {s.specs?.depthPct != null && (
                    <Spec label="Depth" value={`${s.specs.depthPct.toFixed(1)}%`} />
                  )}
                  {s.specs?.tablePct != null && (
                    <Spec label="Table" value={`${s.specs.tablePct.toFixed(1)}%`} />
                  )}
                  {s.specs?.girdle && (
                    <Spec label="Girdle" value={s.specs.girdle} span={2} />
                  )}
                  {/* Crown/pavilion angles (2026-10-21) — supplied-only cert
                      data, degrees, shown only when a feed provided them. */}
                  {s.specs?.crownAngle != null && (
                    <Spec label="Crown" value={`${s.specs.crownAngle.toFixed(1)}°`} />
                  )}
                  {s.specs?.pavilionAngle != null && (
                    <Spec label="Pavilion" value={`${s.specs.pavilionAngle.toFixed(1)}°`} />
                  )}
                  {/* Cert number (#62) — with the grading lab, next to Ratio.
                      spans two cells so a 9-10 digit cert never wraps. */}
                  {s.certNo && (
                    <Spec
                      label={s.lab ? `Cert · ${s.lab}` : 'Cert No.'}
                      value={s.certNo}
                      span={2}
                      href={labReportUrl(s.lab, s.certNo)}
                    />
                  )}
                  {/* Measurements (#60) — full row + nowrap so L × W × H always
                      sits on ONE line (the 3-value string wrapped in a 1/3 cell). */}
                  {s.measurements?.length != null && (
                    <Spec
                      label="Measurements (mm)"
                      value={`${s.measurements.length} × ${s.measurements.width} × ${s.measurements.height}`}
                      span={3}
                    />
                  )}
                </div>

                {/* Diamond tools — pair + layout finders. The finder VIEWS are
                    being built (Python matching service); until they land each
                    button fires an honest "coming soon" toast, never a dead
                    click (cf. the decorative chips removed from the media pane).
                    Blue = action (the ledger accent), distinct from the neutral
                    contact buttons and the save/sourcing verbs in the header. */}
                <div className="grid grid-cols-2 gap-2" data-testid="stone-tools">
                  <StoneToolButton
                    label="Find pairs"
                    onClick={() => toast('Find pairs — coming soon', 'info')}
                    icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M8 2.5 4 7l4 4.5L12 7 8 2.5Z" /><path d="M16 12.5 12 17l4 4.5L20 17l-4-4.5Z" /></svg>}
                  />
                  <StoneToolButton
                    label="Find layouts"
                    onClick={() => toast('Find layouts — coming soon', 'info')}
                    icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1.4" /><rect x="14" y="3" width="7" height="7" rx="1.4" /><rect x="3" y="14" width="7" height="7" rx="1.4" /><rect x="14" y="14" width="7" height="7" rx="1.4" /></svg>}
                  />
                </div>

                {/* v2: the price divider is a THICK rule — the spec sheet's
                    hierarchy anchor separating the stone's facts (hairline
                    spec grid above) from its price (the decision below). */}
                <div className="flex items-end justify-between border-t-2 border-ink pt-5">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-navy-300 font-bold mb-1">Price</p>
                    {blurPrice ? (
                      <p className="text-base font-semibold text-gold-600">Sign in to see price</p>
                    ) : s.priceUSD == null ? (
                      <div data-testid="price-locked">
                        <p className="inline-flex items-center gap-2 text-base font-semibold text-navy-500">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
                          Verification pending
                        </p>
                        <p className="text-xs text-navy-300 mt-0.5">Prices unlock once your account is approved — usually 1–2 business days.</p>
                      </div>
                    ) : (
                      <div className="flex items-baseline gap-2">
                        <p className="text-3xl font-bold text-gold-600 leading-tight tabular tracking-h1">
                          {formatPriceNumber(s.priceUSD)
                            ? <><span className="text-xl font-semibold align-baseline mr-0.5">$</span>{formatPriceNumber(s.priceUSD)}</>
                            : '—'}
                        </p>
                        <p className="text-sm text-navy-500 tabular">{formatPerCarat(s.pricePerCarat)}</p>
                      </div>
                    )}
                  </div>
                  {blurPrice && (
                    <a href="/signin" className="btn-primary px-6">Sign in</a>
                  )}
                </div>

                {/* Seller/contact — the FREE path, and now the ONLY buyer path in
                    the directory (v0): a verified buyer contacts the supplier
                    directly, with no Diaex fee. On DESKTOP this renders in the left
                    column under the image; on MOBILE it renders here, below the
                    price (natural mobile order). The on-platform concierge / buy
                    path was removed for the free-directory launch. */}
                {!blurPrice && s.supplier && (s.supplier.phone || s.supplier.email) && (
                  <div className="md:hidden border-t border-border pt-4">
                    <SupplierContact stone={s} />
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

/**
 * "Listed by" seller block — the FREE contact path (name, contact person,
 * origin, email/phone links + Email/Call/WhatsApp CTAs). Rendered under the
 * image on desktop and between price + Buy-with-Aska on mobile, so it's ONE
 * source of truth for both placements.
 */
function SupplierContact({ stone: s }: { stone: Diamond }) {
  if (!s.supplier) return null
  // Prefilled enquiry (Tej 2026-08-03): every contact tap opens with the
  // stone's identity + interest already written — see lib/contactMessage.
  const msg = stoneEnquiryText(s)
  const subject = stoneEnquirySubject(s)
  return (
    <div data-testid="supplier-contact">
      <p className="text-[10px] uppercase tracking-widest text-soft font-bold mb-2">Listed by</p>
      <div className="flex items-center gap-3 mb-4">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-navy text-white font-serif text-lg font-bold">
          {(s.supplier.name || 'D').charAt(0).toUpperCase()}
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-base font-semibold text-ink truncate">{s.supplier.name}</p>
            <FavoriteSupplierButton stone={s} />
          </div>
          {s.supplier.contactName && s.supplier.contactName !== s.supplier.name && (
            <p className="text-[12px] text-muted mt-0.5 truncate">Attn: {s.supplier.contactName}</p>
          )}
          {s.origin && (
            <p className="text-[12px] text-soft mt-0.5 flex items-start gap-1">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 shrink-0" aria-hidden><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0Z" /><circle cx="12" cy="10" r="3" /></svg>
              <span>{s.origin}</span>
            </p>
          )}
          <p className="text-[12px] text-soft mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
            {s.supplier.email && (
              <a href={mailtoHref(s.supplier.email, subject, msg)} onClick={() => logEnquiry(s.stockId, 'email', s.uniqueId)}
                className="text-info underline underline-offset-2 break-all">{s.supplier.email}</a>
            )}
            {s.supplier.phone && (
              <a href={`tel:${s.supplier.phone}`} onClick={() => logEnquiry(s.stockId, 'call', s.uniqueId)}
                className="text-info underline underline-offset-2 tabular">{s.supplier.phone}</a>
            )}
          </p>
          {supplierReturnsLine(s.supplier.returns) && (
            <p className="text-[12px] text-soft mt-0.5" data-testid="supplier-returns-line">
              {supplierReturnsLine(s.supplier.returns)}
              <span className="text-muted"> · supplier's own policy for direct deals</span>
            </p>
          )}
        </div>
      </div>

      {/* 2×2 contact grid — WhatsApp/email/SMS open PREFILLED with the stone
          id + interest; Call stays a plain dial. */}
      <div className="grid grid-cols-2 gap-2">
        {s.supplier.email ? (
          <a href={mailtoHref(s.supplier.email, subject, msg)} onClick={() => logEnquiry(s.stockId, 'email', s.uniqueId)}
            className="inline-flex items-center justify-center gap-1.5 h-10 rounded-full border border-border text-ink text-sm font-semibold no-underline hover:bg-page transition-colors">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>
            Email
          </a>
        ) : (
          <span className="inline-flex items-center justify-center h-10 rounded-full border border-border text-soft/60 text-sm font-semibold">Email</span>
        )}
        {s.supplier.phone ? (
          <a href={`tel:${s.supplier.phone}`} onClick={() => logEnquiry(s.stockId, 'call', s.uniqueId)}
            className="inline-flex items-center justify-center gap-1.5 h-10 rounded-full border border-border text-ink text-sm font-semibold no-underline hover:bg-page transition-colors">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.1-8.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.6a2 2 0 0 1-.5 2.1L8.1 9.5a16 16 0 0 0 6 6l1.1-1.1a2 2 0 0 1 2.1-.5c.8.3 1.7.5 2.6.6a2 2 0 0 1 1.7 2Z" /></svg>
            Call
          </a>
        ) : (
          <span className="inline-flex items-center justify-center h-10 rounded-full border border-border text-soft/60 text-sm font-semibold">Call</span>
        )}
        {waTargetOf(s.supplier) ? (
          <a href={waHref(waTargetOf(s.supplier)!, msg)} target="_blank" rel="noopener"
            onClick={() => logEnquiry(s.stockId, 'whatsapp', s.uniqueId)}
            className="inline-flex items-center justify-center gap-1.5 h-10 rounded-full bg-navy border border-border text-white text-sm font-semibold no-underline hover:bg-navy-hover transition-colors">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 0 0-8.5 15.3L2 22l4.8-1.4A10 10 0 1 0 12 2Zm5.3 14.1c-.2.6-1.3 1.2-1.8 1.2-.5.1-1 .1-1.7-.1a12 12 0 0 1-5.3-3.6 6.6 6.6 0 0 1-1.4-3c0-.6.3-1.3.6-1.6.2-.2.5-.3.7-.3h.5c.2 0 .4 0 .6.5l.7 1.7c.1.2 0 .4-.1.6l-.4.5c-.1.2-.3.3-.1.6a9 9 0 0 0 3.7 3.2c.3.1.5.1.6-.1l.6-.7c.2-.3.4-.2.6-.1l1.6.8c.2.1.4.2.4.3.1.2.1.6-.1 1.2Z" /></svg>
            WhatsApp
          </a>
        ) : (
          <span className="inline-flex items-center justify-center h-10 rounded-full bg-navy/40 border border-border text-white/70 text-sm font-semibold">WhatsApp</span>
        )}
        {s.supplier.phone ? (
          <a href={smsHref(s.supplier.phone, msg)} onClick={() => logEnquiry(s.stockId, 'call', s.uniqueId)}
            className="inline-flex items-center justify-center gap-1.5 h-10 rounded-full border border-border text-ink text-sm font-semibold no-underline hover:bg-page transition-colors"
            data-testid="contact-sms">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
            Text
          </a>
        ) : (
          <span className="inline-flex items-center justify-center h-10 rounded-full border border-border text-soft/60 text-sm font-semibold">Text</span>
        )}
      </div>
    </div>
  )
}

function LoadingPill({ text }: { text: string }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <span className="inline-flex items-center gap-2 rounded-full bg-paper/90 px-3 py-1.5 text-[11px] font-semibold text-ink shadow-card">
        <svg className="animate-spin" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12a9 9 0 11-6.22-8.56" />
        </svg>
        {text}
      </span>
    </div>
  )
}

function MediaToggle({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`rounded-full px-3.5 h-8 text-xs font-semibold transition-colors ${
        active ? 'bg-navy text-white' : 'text-ink hover:bg-page'
      }`}
    >
      {children}
    </button>
  )
}

/**
 * A diamond-tool action pill (Find pairs / Find layouts). Sapphire ledger
 * accent so it reads as an action, set apart from the neutral contact buttons.
 * The matching VIEWS are still being built, so callers wire an honest
 * "coming soon" toast rather than a dead click.
 */
function StoneToolButton({ label, icon, onClick }: { label: string; icon: React.ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center justify-center gap-1.5 h-10 rounded-full border border-sapphire/30 bg-sapphire/[0.06] text-sapphire text-sm font-semibold hover:bg-sapphire/10 transition-colors"
    >
      {icon}
      {label}
    </button>
  )
}

function Spec({ label, value, span, href }: { label: string; value?: string | null; span?: 2 | 3; href?: string | null }) {
  // span=3 (full row) values render on one line — used by Measurements so the
  // L × W × H string never wraps inside a 1/3-width cell (#60).
  const spanCls = span === 3 ? 'col-span-3' : span === 2 ? 'col-span-2' : ''
  const text = value || '—'
  return (
    <div className={`leading-tight ${spanCls}`}>
      <span className="text-[10px] uppercase tracking-wider text-navy-300">{label}</span>
      <p className={`text-sm font-semibold text-ink tabular ${span ? 'whitespace-nowrap' : ''}`}>
        {href
          ? (
            <a href={href} target="_blank" rel="noopener noreferrer" className="underline decoration-dotted underline-offset-2 hover:text-gold-600">
              {text}
            </a>
          )
          : text}
      </p>
    </div>
  )
}
